import { useMemo } from "react";
import { Building2, Filter, X } from "lucide-react";
import { extensionTotal } from "@/lib/calculations";
import { parseRefId } from "@/lib/actuals";
import { useFormatCurrency } from "@/lib/CurrencyContext";

/**
 * Per-extension planned-vs-actual strip for Actual mode → Extensions tab.
 * Mirrors RoomPlanVsActual: each row is plan / actual / bar / Δ%, click
 * filters the entries list below to that extension.
 */
export default function ExtensionPlanVsActual({ project, activeExtId, onToggleExt }) {
  const fmt = useFormatCurrency();
  const extensions = project.extensions || [];
  const actuals = project.actuals || [];

  const { rows, unassigned } = useMemo(() => {
    const extIds = new Set(extensions.map((e) => e.id));
    let unassignedSum = 0;
    const actualByExt = new Map();
    for (const a of actuals) {
      if ((a.category || "other") !== "extensions") continue;
      const ref = parseRefId(a.ref_id);
      if (ref && ref.kind === "ext" && extIds.has(ref.extId)) {
        actualByExt.set(
          ref.extId,
          (actualByExt.get(ref.extId) || 0) + (Number(a.amount) || 0)
        );
      } else {
        unassignedSum += Number(a.amount) || 0;
      }
    }
    const rowsOut = extensions.map((e) => ({
      id: e.id,
      label: e.name || humanise(e.type),
      planned: extensionTotal(e),
      actual: actualByExt.get(e.id) || 0,
    }));
    return { rows: rowsOut, unassigned: unassignedSum };
  }, [extensions, actuals]);

  if (rows.length === 0 && unassigned === 0) return null;

  return (
    <div className="fc-card p-5" data-testid="extension-plan-vs-actual">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="fc-label">Per-extension plan vs actual</div>
          <p className="text-xs text-[#78716C] mt-1">Click a row to filter the entries list below.</p>
        </div>
        {activeExtId && (
          <button
            type="button"
            onClick={() => onToggleExt?.(null)}
            data-testid="extension-filter-clear"
            className="text-[11px] text-slate-brand-700 border border-slate-brand-200 bg-slate-brand-50 hover:bg-slate-brand-100 rounded-full h-7 px-2.5 inline-flex items-center gap-1 transition-colors"
          >
            <X className="w-3 h-3" /> Clear filter
          </button>
        )}
      </div>

      <div className="mt-4 space-y-2" data-testid="extension-breakdown-list">
        {rows.map((row) => (
          <Row
            key={row.id}
            row={row}
            fmt={fmt}
            active={activeExtId === row.id}
            onClick={() => onToggleExt?.(activeExtId === row.id ? null : row.id)}
          />
        ))}
        {unassigned > 0 && (
          <Unassigned
            amount={unassigned}
            fmt={fmt}
            active={activeExtId === "__unassigned__"}
            onClick={() =>
              onToggleExt?.(activeExtId === "__unassigned__" ? null : "__unassigned__")
            }
          />
        )}
      </div>
    </div>
  );
}

function Row({ row, fmt, active, onClick }) {
  const pct = row.planned > 0 ? (row.actual / row.planned) * 100 : row.actual > 0 ? 100 : 0;
  const over = row.planned > 0 && row.actual > row.planned;
  const bar = over ? "bg-[#DC2626]" : pct > 85 ? "bg-slate-brand-500" : "bg-slate-brand-300";
  const delta = row.actual - row.planned;
  const deltaPct = row.planned > 0 ? (delta / row.planned) * 100 : 0;

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`extension-row-${row.id}`}
      className={`w-full text-left rounded-lg px-3 py-2.5 border transition-colors ${
        active
          ? "fc-slate-highlight"
          : "border-transparent hover:bg-slate-brand-50 hover:border-slate-brand-200"
      }`}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="inline-flex items-center gap-2 min-w-0">
          <Building2 className="w-3.5 h-3.5 text-slate-brand-500 shrink-0" />
          <span className="text-[13px] font-medium text-[#1C1C1C] truncate">{row.label}</span>
        </div>
        <div className="flex items-center gap-3 fc-mono text-xs shrink-0">
          <span className="text-[#A8A29E]">{fmt(row.planned)}</span>
          <span className="text-[#A8A29E]">→</span>
          <span className={over ? "text-[#DC2626]" : "text-[#1C1C1C]"}>{fmt(row.actual)}</span>
          <span
            className={`text-[11px] w-16 text-right ${
              over ? "text-[#DC2626]" : row.actual === 0 ? "text-[#A8A29E]" : "text-slate-brand-700"
            }`}
          >
            {row.planned > 0
              ? `${delta >= 0 ? "+" : ""}${deltaPct.toFixed(0)}%`
              : row.actual > 0
              ? "no plan"
              : "—"}
          </span>
        </div>
      </div>
      <div className="mt-2 h-1.5 w-full bg-black/[0.05] rounded-full overflow-hidden">
        <div className={`h-full ${bar} transition-all`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
    </button>
  );
}

function Unassigned({ amount, fmt, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="extension-row-unassigned"
      className={`w-full text-left rounded-lg px-3 py-2.5 border transition-colors ${
        active
          ? "fc-slate-highlight"
          : "border-transparent hover:bg-slate-brand-50 hover:border-slate-brand-200"
      }`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="inline-flex items-center gap-2 text-[13px] font-medium text-[#1C1C1C]">
          <Filter className="w-3.5 h-3.5 text-slate-brand-500 shrink-0" />
          Unassigned
          <span className="text-[11px] text-[#78716C] font-normal">
            (entries not tagged to an extension)
          </span>
        </div>
        <div className="fc-mono text-xs text-[#1C1C1C]">{fmt(amount)}</div>
      </div>
    </button>
  );
}

function humanise(t = "") {
  return t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
